import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { EntityDocuments } from "@/pages/documents/EntityDocuments";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { IdCard, Clock, Users, Wallet, Home, GraduationCap, FileText, StickyNote, Plus, Trash2, Camera, Upload, Check, X, Pencil, Archive } from "lucide-react";
import { toast } from "sonner";
import EntityDialog from "@/pages/people/EntityDialog";

const CARDC = { active: "bg-emerald-100 text-emerald-700", suspended: "bg-amber-100 text-amber-700", lost: "bg-red-100 text-red-700", replaced: "bg-slate-100 text-slate-500" };
const RIGHTS = ["can_view_grades", "can_view_finance", "can_view_documents", "can_pickup", "is_primary",
  "is_legal_guardian", "is_payer", "can_sign_documents", "can_receive_reminders", "can_submit_documents", "can_receive_urgent_notifications"];

export default function StudentProfile() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const { id } = useParams();
  const canManage = hasRole("super_admin", "direction", "administration", "coordinateur");
  const [p, setP] = useState(null);
  const [rights, setRights] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [photo, setPhoto] = useState(null);
  const fileRef = useRef();
  const [editOpen, setEditOpen] = useState(false);
  const saveEdit = async (v) => { try { await api.put(`/people/students/${id}`, { name: v.name, birthdate: v.birthdate, status: v.status }); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const archiveStudent = async () => { if (!window.confirm(t("archiveConfirm"))) return; try { await api.patch(`/people/students/${id}/archive`); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const [backend] = useState(process.env.REACT_APP_BACKEND_URL);

  const load = useCallback(() => {
    api.get(`/people/students/${id}/profile`).then((r) => setP(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
    api.get(`/people/students/${id}/parent-rights`).then((r) => setRights(r.data)).catch(() => {});
    api.get(`/people/students/${id}/timeline`).then((r) => setTimeline(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canManage) api.get(`/people/students/${id}/notes`).then((r) => setNotes(r.data)).catch(() => {}); }, [id, canManage]);
  useEffect(() => { if (canManage) api.get(`/people/students/${id}/photo`).then((r) => setPhoto(r.data)).catch(() => {}); }, [id, canManage]);

  const refreshPhoto = () => api.get(`/people/students/${id}/photo`).then((r) => setPhoto(r.data)).catch(() => {});
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/people/students/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(t("save")); refreshPhoto(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    e.target.value = "";
  };
  const validatePhoto = async () => { try { await api.patch(`/people/students/${id}/photo/${photo.id}/validate`); refreshPhoto(); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const rejectPhoto = async () => { const reason = window.prompt(`${t("rejectReason")} :`); if (!reason) return; try { await api.patch(`/people/students/${id}/photo/${photo.id}/reject`, { reason }); refreshPhoto(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const toggleRight = async (pr, key) => {
    const next = { ...pr, [key]: !pr[key] };
    setRights((rs) => rs.map((x) => x.parent_id === pr.parent_id ? next : x));
    try { await api.put(`/people/students/${id}/parent-rights`, { parent_id: pr.parent_id, ...next }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); load(); }
  };
  const issueCard = async () => { try { await api.post(`/people/students/${id}/card`); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const cardStatus = async (status) => {
    const reason = status === "active" ? "" : (window.prompt(`${t("reason")} :`) || "");
    try { await api.patch(`/people/cards/${p.card.id}/status`, { status, reason }); toast.success(t("save")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await api.post(`/people/students/${id}/notes`, { text: noteText }); setNoteText(""); api.get(`/people/students/${id}/notes`).then((r) => setNotes(r.data)); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delNote = async (nid) => { try { await api.delete(`/people/students/${id}/notes/${nid}`); api.get(`/people/students/${id}/notes`).then((r) => setNotes(r.data)); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  if (!p) return <div className="text-slate-400">{t("loading")}</div>;
  return (
    <div data-testid="student-profile">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-900">{p.name}</h1>
          <p className="text-sm text-slate-500">{p.level_name || "—"} · {p.current_group?.group_name || t("noData")} · {p.family?.name || ""}</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1" data-testid="student-edit-btn" onClick={() => setEditOpen(true)}><Pencil className="h-4 w-4" />{t("editStudent")}</Button>
            <Button size="sm" variant="outline" className="gap-1" data-testid="student-archive-btn" onClick={archiveStudent}><Archive className="h-4 w-4" />{t("archive")}</Button>
          </div>
        )}
      </div>
      <EntityDialog open={editOpen} onOpenChange={setEditOpen} title={t("editStudent")} onSubmit={saveEdit}
        initial={{ name: p.name, birthdate: p.birthdate, status: p.status }}
        fields={[{ name: "name", label: t("name") }, { name: "birthdate", label: t("birthdate"), inputType: "date" }, { name: "status", label: t("status") }]} />
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview"><GraduationCap className="h-4 w-4 mr-2" />{t("overview")}</TabsTrigger>
          <TabsTrigger value="family" data-testid="tab-family"><Home className="h-4 w-4 mr-2" />{t("familyAndParents")}</TabsTrigger>
          <TabsTrigger value="parents" data-testid="tab-parents"><Users className="h-4 w-4 mr-2" />{t("parentsRightsFull")}</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline"><Clock className="h-4 w-4 mr-2" />{t("timeline")}</TabsTrigger>
          <TabsTrigger value="card" data-testid="tab-card"><IdCard className="h-4 w-4 mr-2" />{t("studentCard")}</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-sdocuments"><FileText className="h-4 w-4 mr-2" />{t("documentsTab")}</TabsTrigger>
          <TabsTrigger value="wallet" data-testid="tab-wallet"><Wallet className="h-4 w-4 mr-2" />Wallet</TabsTrigger>
          {canManage && <TabsTrigger value="notes" data-testid="tab-snotes"><StickyNote className="h-4 w-4 mr-2" />{t("internalNotes")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview">
          <Card className="border-slate-200 p-5 max-w-lg space-y-2 text-sm" data-testid="student-overview">
            <div className="flex justify-between items-center"><span className="text-slate-500">{t("level")}</span><span className="text-slate-800">{p.level_name || "—"}</span></div>
            <div className="flex justify-between items-center"><span className="text-slate-500">{t("group")}</span>
              {p.current_group?.group_name ? <Link to="/school/groups" data-testid="student-group-link"><Button size="sm" variant="outline">{p.current_group.group_name}</Button></Link> : <span>—</span>}
            </div>
            <div className="flex justify-between items-center"><span className="text-slate-500">{t("family")}</span>
              {p.family ? <Link to={`/people/families/${p.family.id}`} data-testid="student-family-link"><Button size="sm" variant="outline" className="gap-1"><Home className="h-3.5 w-3.5" />{p.family.name}</Button></Link> : <span>—</span>}
            </div>
            <div className="flex justify-between items-center"><span className="text-slate-500">{t("cardStatus")}</span>
              {p.card ? <Badge className={`${CARDC[p.card.status] || "bg-slate-100"} border-0`}>{p.card.status}</Badge> : <span className="text-slate-400">{t("noCard")}</span>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="family">
          <div className="space-y-3" data-testid="student-family-tab">
            {p.family && (
              <Card className="border-slate-200 p-4 flex items-center justify-between">
                <div><div className="font-semibold text-slate-800">{p.family.name}</div><div className="text-xs text-slate-500">{p.family.email || "—"} · {p.family.phone || "—"}</div></div>
                <Link to={`/people/families/${p.family.id}`}><Button size="sm" variant="outline" className="gap-1"><Home className="h-4 w-4" />{t("viewFamily")}</Button></Link>
              </Card>
            )}
            {(p.parents || []).map((pr) => (
              <Card key={pr.id} className="border-slate-200 p-4 flex items-center justify-between" data-testid="student-parent-card">
                <div><div className="font-medium text-slate-800">{pr.name}</div><div className="text-xs text-slate-500">{pr.email || "—"} · {pr.phone || "—"}</div></div>
                {canManage && <Link to={`/people/parents/${pr.id}`} data-testid={`student-parent-link-${pr.id}`}><Button size="sm" variant="outline">{t("viewParent")}</Button></Link>}
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="parents">
          <div className="space-y-3">{rights.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
            rights.map((pr) => (
              <Card key={pr.parent_id} className="border-slate-200 p-4" data-testid="parent-rights-card">
                <div className="font-semibold text-slate-800 mb-3">{pr.parent_name}{pr.is_primary && <Badge className="ml-2 bg-sky-100 text-sky-700 border-0">{t("primary")}</Badge>}</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {RIGHTS.map((k) => (
                    <label key={k} className="flex items-center justify-between text-sm text-slate-600 rounded-lg border border-slate-100 px-3 py-2">
                      {t(k)}
                      <Switch data-testid={`right-${k}`} checked={!!pr[k]} disabled={!canManage} onCheckedChange={() => toggleRight(pr, k)} />
                    </label>
                  ))}
                </div>
              </Card>))}
          </div>
        </TabsContent>

        <TabsContent value="timeline">
          <div className="space-y-2" data-testid="timeline-list">{timeline.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
            timeline.map((it, i) => (
              <div key={i} className="flex gap-3 items-start rounded-lg border border-slate-100 px-3 py-2">
                <div className="h-2 w-2 rounded-full bg-primary mt-2" />
                <div><div className="text-sm font-medium text-slate-800">{it.title}</div>
                  <div className="text-xs text-slate-500">{(it.date || "").slice(0, 16).replace("T", " ")}{it.detail ? ` · ${it.detail}` : ""}</div></div>
              </div>))}
          </div>
        </TabsContent>

        <TabsContent value="card">
          {canManage && (
            <Card className="border-slate-200 p-5 max-w-md mb-3" data-testid="student-photo">
              <div className="flex items-center gap-4">
                <div className="h-24 w-20 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
                  {photo?.has_photo
                    ? <img src={`${backend}/api/people/students/${id}/photo/image?t=${photo.validated_at || photo.uploaded_at || photo.photo_status}`} alt="photo" className="h-full w-full object-cover" data-testid="student-photo-img" />
                    : <Camera className="h-6 w-6 text-slate-300" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-800">{t("studentPhoto")}</div>
                  <Badge className="border-0 bg-slate-100 mt-1" data-testid="photo-status">{photo?.photo_status || "Non fournie"}</Badge>
                  {photo?.rejected_reason && <p className="text-xs text-red-500 mt-1">{photo.rejected_reason}</p>}
                  {!photo?.has_photo && <p className="text-xs text-slate-400 mt-1">{t("noPhoto")}</p>}
                  <input type="file" accept="image/*" hidden ref={fileRef} onChange={uploadPhoto} data-testid="photo-file-input" />
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button size="sm" variant="secondary" className="gap-1" data-testid="photo-upload-btn" onClick={() => fileRef.current?.click()}><Upload className="h-3.5 w-3.5" />{t("uploadPhoto")}</Button>
                    {photo?.has_photo && photo.photo_status !== "Validée" && <Button size="sm" className="gap-1" data-testid="photo-validate-btn" onClick={validatePhoto}><Check className="h-3.5 w-3.5" />{t("validatePhoto")}</Button>}
                    {photo?.has_photo && <Button size="sm" variant="outline" className="gap-1" data-testid="photo-reject-btn" onClick={rejectPhoto}><X className="h-3.5 w-3.5" />{t("rejectPhoto")}</Button>}
                  </div>
                </div>
              </div>
            </Card>
          )}
          <Card className="border-slate-200 p-6 max-w-md" data-testid="student-card">
            {!p.card ? (
              <div className="text-center">
                <p className="text-sm text-slate-500 mb-4">{t("noCard")}</p>
                {canManage && <Button data-testid="issue-card-btn" onClick={issueCard}>{t("issueCard")}</Button>}
              </div>
            ) : (
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <span className="font-mono text-slate-700">{p.card.card_uid}</span>
                  <Badge className={`${CARDC[p.card.status] || "bg-slate-100"} border-0`} data-testid="card-status">{p.card.status}</Badge>
                </div>
                <img src={`${backend}/api/people/cards/${p.card.id}/qr.png`} alt="QR" className="mx-auto h-44 w-44 rounded-lg border border-slate-100 bg-white p-2" data-testid="card-qr" />
                {canManage && <div className="mt-3"><a href={`${backend}/api/people/cards/${p.card.id}/pdf`} target="_blank" rel="noreferrer"><Button size="sm" variant="secondary" data-testid="card-pdf-btn">{t("downloadCardPdf")}</Button></a></div>}
                {canManage && (
                  <div className="flex flex-wrap justify-center gap-2 mt-4">
                    <Button size="sm" variant="outline" data-testid="card-suspend" onClick={() => cardStatus("suspended")}>{t("suspend")}</Button>
                    <Button size="sm" variant="outline" data-testid="card-activate" onClick={() => cardStatus("active")}>{t("activate")}</Button>
                    <Button size="sm" variant="outline" data-testid="card-lost" onClick={() => cardStatus("lost")}>{t("cardLost")}</Button>
                    <Button size="sm" variant="outline" data-testid="card-replace" onClick={() => cardStatus("replaced")}>{t("cardReplace")}</Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <EntityDocuments filterKey="student_id" id={id} testid="student-documents" />
        </TabsContent>

        <TabsContent value="wallet">
          <Card className="border-slate-200 p-6 max-w-md" data-testid="student-wallet">
            <div className="flex items-center gap-3">
              <Wallet className="h-8 w-8 text-primary" />
              <div><div className="text-2xl font-bold text-slate-900">{p.wallet?.points_balance ?? 0} pts</div>
                <div className="text-xs text-slate-500">{t("walletComingSoon")}</div></div>
            </div>
          </Card>
        </TabsContent>

        {canManage && (
          <TabsContent value="notes">
            <Card className="border-slate-200 p-4 mb-3">
              <Textarea data-testid="snote-input" placeholder={t("noteText")} value={noteText} onChange={(e) => setNoteText(e.target.value)} className="mb-2" />
              <Button data-testid="snote-add-btn" onClick={addNote} className="gap-1"><Plus className="h-4 w-4" />{t("addNote")}</Button>
            </Card>
            <div className="space-y-2" data-testid="student-notes-list">
              {notes.length === 0 ? <p className="text-sm text-slate-400">{t("noNotes")}</p> :
                notes.map((n) => (
                  <Card key={n.id} className="border-slate-200 p-3 flex items-start justify-between" data-testid="snote-item">
                    <div><div className="text-sm text-slate-800">{n.text}</div><div className="text-xs text-slate-400">{n.author_name} · {(n.created_at || "").slice(0, 16).replace("T", " ")}</div></div>
                    <Button size="icon" variant="ghost" data-testid={`snote-del-${n.id}`} onClick={() => delNote(n.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </Card>))}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
